import { useRef, useState } from "react";
import { UploadCloud, FileText, X, FolderOpen } from "lucide-react";

export default function UploadZone({ testid, files, setFiles, hint, exts = [".pdf"], accept = "application/pdf" }) {
  const filesInputRef = useRef(null);
  const folderInputRef = useRef(null);
  const [drag, setDrag] = useState(false);

  const addFiles = (list) => {
    const ok = Array.from(list).filter((f) => exts.some((e) => f.name.toLowerCase().endsWith(e)));
    setFiles((prev) => {
      const names = new Set(prev.map((f) => f.name + f.size));
      return [...prev, ...ok.filter((f) => !names.has(f.name + f.size))];
    });
  };

  // Recursively read a dropped directory entry (folder drag & drop)
  const readEntry = (entry, out) =>
    new Promise((resolve) => {
      if (!entry) return resolve();
      if (entry.isFile) {
        entry.file((file) => {
          out.push(file);
          resolve();
        }, () => resolve());
      } else if (entry.isDirectory) {
        const reader = entry.createReader();
        const readBatch = () =>
          reader.readEntries(async (entries) => {
            if (!entries.length) return resolve();
            await Promise.all(entries.map((e) => readEntry(e, out)));
            readBatch();
          }, () => resolve());
        readBatch();
      } else resolve();
    });

  const onDrop = async (e) => {
    e.preventDefault();
    setDrag(false);
    const items = e.dataTransfer.items;
    if (items && items.length && items[0].webkitGetAsEntry) {
      const out = [];
      const entries = Array.from(items)
        .map((it) => (it.webkitGetAsEntry ? it.webkitGetAsEntry() : null))
        .filter(Boolean);
      await Promise.all(entries.map((en) => readEntry(en, out)));
      if (out.length) {
        addFiles(out);
        return;
      }
    }
    addFiles(e.dataTransfer.files);
  };

  return (
    <div>
      <div
        data-testid={testid}
        onClick={() => filesInputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          setDrag(true);
        }}
        onDragLeave={() => setDrag(false)}
        onDrop={onDrop}
        className={`cursor-pointer border border-dashed rounded-sm px-6 py-8 text-center transition-colors ${
          drag ? "border-primary bg-muted/40" : "border-border hover:border-muted-foreground bg-card"
        }`}
      >
        <UploadCloud className="mx-auto text-muted-foreground" size={26} />
        <div className="mt-3 text-sm font-medium">Trage fișierele / folderul aici sau click pentru a selecta</div>
        <div className="mt-1 text-xs text-muted-foreground font-mono">{hint || "Selectează fișiere individuale sau un folder întreg"}</div>

        <button
          type="button"
          data-testid={`${testid}-folder`}
          onClick={(e) => {
            e.stopPropagation();
            folderInputRef.current?.click();
          }}
          className="mt-4 inline-flex items-center gap-2 border border-border rounded-sm px-3 py-1.5 text-xs font-mono hover:bg-muted transition-colors"
        >
          <FolderOpen size={13} />
          Selectează un folder
        </button>

        <input
          ref={filesInputRef}
          type="file"
          accept={accept}
          multiple
          className="hidden"
          onChange={(e) => addFiles(e.target.files)}
        />
        <input
          ref={folderInputRef}
          type="file"
          multiple
          webkitdirectory=""
          directory=""
          className="hidden"
          onChange={(e) => addFiles(e.target.files)}
        />
      </div>

      {files.length > 0 && (
        <div className="mt-3">
          <div className="text-xs text-muted-foreground font-mono mb-1.5">{files.length} fișiere selectate</div>
          <div className="space-y-1.5 max-h-40 overflow-auto">
            {files.map((f, i) => (
              <div
                key={f.name + i}
                className="flex items-center justify-between bg-card border border-border rounded-sm px-3 py-1.5 text-xs"
              >
                <span className="flex items-center gap-2 truncate font-mono">
                  <FileText size={13} className="text-muted-foreground shrink-0" />
                  <span className="truncate">{f.name}</span>
                </span>
                <button
                  onClick={() => setFiles((prev) => prev.filter((_, idx) => idx !== i))}
                  className="text-muted-foreground hover:text-[#FF3B30] shrink-0"
                >
                  <X size={13} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
